import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash.debounce';
import ProductSelectionCard from './ProductSelectionCard';
import * as editableDocument from 'utils/model/editableDocument';
import { min, valueChanged, when } from 'utils/fn-combinators';
import { isInRange, isMandatory } from 'utils/simple-validations';
import flow from 'lodash.flow';
import {
  chooseFavoriteProduct,
  computeMaxResidualValuePercent,
  deleteProduct,
  loadProductSelectionData,
  recomputeSimulations,
  selectSimulation,
  setFinancialProductsViewMode
} from '../actionCreators';
import { withTranslation } from 'react-i18next';
import ContractSelectionModal from './ContractSelectionModal';
import * as actionTypes from '../actionTypes';

const getValidationRules = document => ({
  financingPeriod: {
    value: [isMandatory(), isInRange(document.financingPeriod.minValue, document.financingPeriod.maxValue)]
  },
  advancePercent: {
    value: [isMandatory(), isInRange(document.advancePercent.minValue, document.advancePercent.maxValue)]
  },
  residualValuePercent: {
    value: [isMandatory(), isInRange(document.residualValuePercent.minValue, document.residualValuePercent.maxValue)]
  }
});

const getBusinessRules = () => ({
  residualValuePercent: {
    value: when(
      valueChanged(document => document.advancePercent.value),
      min(
        document => document.residualValuePercent.value,
        document => 100 - document.advancePercent.value
      )
    )
  },
  advancePercent: {
    value: when(
      valueChanged(document => document.residualValuePercent.value),
      min(
        document => document.advancePercent.value,
        document => 100 - document.residualValuePercent.value
      )
    )
  }
});

 
const computeMaxResidualValuePercentDebounced = debounce((dispatch, documentId, siteId, financingPeriod) => {
  dispatch(computeMaxResidualValuePercent(documentId, siteId, financingPeriod));
}, 1000);

const ProductSelectionContainer = ({ documentId, siteId, t }) => {
  const dispatch = useDispatch();
  const productSelectionData = useSelector(state => state.currentOffer.productSelectionData, shallowEqual);
  const processing = useSelector(state => state.currentOffer.header.processing, shallowEqual);
  const productsViewMode = useSelector(state => state.currentOffer.productSelectionData.flags.viewMode || 0, shallowEqual);
  const params = useSelector(state => state.params.items, shallowEqual);
  const [localFilters, setLocalFilters] = useState();
  const [showFinancialProductAddModal, setShowFinancialProductAddModal] = useState(false);
  const [showContractTypesModal, setShowContractTypeModal] = useState(false);
  const [selectedContractTypes, setSelectedContractTypes] = useState([]);
  const [products, setProducts] = useState(productSelectionData.products);
  const [orderObject, setOrderObject] = useState({
    orderId: 1,
    orderName: `${t('OfferEdit.FinancialProducts.RateWithoutVAT')} - ${t('Tooltips.OrderAscending')}`
  });

  const orderOptions = useMemo(
    () => [
      { orderId: 1, orderName: `${t('OfferEdit.FinancialProducts.RateWithoutVAT')} - ${t('Tooltips.OrderAscending')}` },
      { orderId: 2, orderName: `${t('OfferEdit.FinancialProducts.RateWithoutVAT')} - ${t('Tooltips.OrderDescending')}` },
      { orderId: 3, orderName: `${t('OfferEdit.FinancialProducts.ContractAmountWithoutVAT')} - ${t('Tooltips.OrderAscending')}` },
      {
        orderId: 4,
        orderName: `${t('OfferEdit.FinancialProducts.ContractAmountWithoutVAT')} - ${t('Tooltips.OrderDescending')}`
      },
      { orderId: 5, orderName: `${t('OfferEdit.FinancialProducts.Favorites')}` }
    ],
    [t]
  );

  const onOrderChange = useCallback(
    orderId => {
      const order = orderOptions.find(option => option.orderId === orderId);
      setOrderObject(order);
    },
    [orderOptions]
  );

  const setProductsViewMode = useCallback(value => dispatch(setFinancialProductsViewMode(value)), [dispatch]);

  useEffect(() => {
    if (!(orderObject && productSelectionData.products)) return;
    const products = JSON.parse(JSON.stringify(productSelectionData.products));

    if (!productSelectionData.flags.computingSimulations) {
      const sortProp = orderObject.orderName.split(' - ')[0];
      const sortType = orderObject.orderName.split(' - ')[1];
      if (sortProp === t('OfferEdit.FinancialProducts.RateWithoutVAT'))
        products.sort(
          sortType === t('Tooltips.OrderAscending')
            ? (a, b) =>
                isNaN(a.monthlyRateValueWithoutVat)
                  ? 1 - isNaN(b.monthlyRateValueWithoutVat)
                  : a.monthlyRateValueWithoutVat - b.monthlyRateValueWithoutVat
            : (a, b) =>
                isNaN(b.monthlyRateValueWithoutVat)
                  ? 1 - isNaN(a.monthlyRateValueWithoutVat)
                  : b.monthlyRateValueWithoutVat - a.monthlyRateValueWithoutVat
        );

      if (sortProp === t('OfferEdit.FinancialProducts.ContractAmountWithoutVAT'))
        products.sort(
          sortType === t('Tooltips.OrderAscending')
            ? (a, b) => (isNaN(a.contractValue) ? 1 - isNaN(b.contractValue) : a.contractValue - b.contractValue)
            : (a, b) => (isNaN(b.contractValue) ? 1 - isNaN(a.contractValue) : b.contractValue - a.contractValue)
        );

      if (sortProp === t('OfferEdit.FinancialProducts.Favorites')) {
        products.sort((a, b) => (isNaN(b.priority) ? 1 - isNaN(a.priority) : b.priority - a.priority));
      }
    }
    setProducts(products);
  }, [orderObject, productSelectionData.flags.computingSimulations, productSelectionData.products, t]);

  useEffect(() => {
    if (!productSelectionData.flags.productDataLoaded)
      dispatch(
        loadProductSelectionData(
          documentId,
          siteId,
          selectedContractTypes.filter(x => x.isSelected === true)
        )
      );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (productSelectionData.flags.productDataLoaded) setSelectedContractTypes(productSelectionData.contractTypes);
  }, [productSelectionData.contractTypes, productSelectionData.flags.productDataLoaded]);

  useEffect(() => {
    switch (true) {
      case !productSelectionData.flags.productDataLoaded:
        dispatch(
          loadProductSelectionData(
            documentId,
            siteId,
            selectedContractTypes.filter(x => x.isSelected === true)
          )
        );
        break;
      case !productSelectionData.simulationsGenerated:
        if (
          productSelectionData.contractTypes.filter(x => x.isSelected === true).length === 0 &&
          productSelectionData.showFilterOfferContracts
        ) {
          setShowContractTypeModal(true);
          break;
        } else if (productSelectionData.contractTypes.length === 1) {
          setSelectedContractTypes(productSelectionData.contractTypes);
          break;
        }
        break;
      default:
        setSelectedContractTypes(productSelectionData.contractTypes);
    }
  }, [productSelectionData.flags.productDataLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (productSelectionData.flags.productDataLoaded && productSelectionData.filterDefinition != null) {
      const doc = editableDocument.create(productSelectionData.filterDefinition);
      setLocalFilters(doc);
    }
  }, [productSelectionData.filterDefinition, productSelectionData.flags.productDataLoaded]);

  useEffect(() => {
    if (localFilters && !isNaN(productSelectionData.rezidualValueComputed)) {
      setLocalFilters(prevState => {
        let doc = flow(
          editableDocument.update('residualValuePercent.value', productSelectionData.rezidualValueComputed, getBusinessRules()),
          editableDocument.update('residualValuePercent.maxValue', productSelectionData.maxRezidualValueComputed, getBusinessRules())
        )(prevState);

        doc = editableDocument.validate(getValidationRules(doc.document), true, doc);

        return doc;
      });
    }
  }, [productSelectionData.maxRezidualValueComputed, productSelectionData.rezidualValueComputed]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPushFilterValues = () => {
    setLocalFilters(prevState => editableDocument.resetDirtyInfo(prevState));
    const doc = localFilters.document;
    dispatch(
      recomputeSimulations(
        documentId,
        siteId,
        doc.financingPeriod.value,
        doc.advancePercent.value,
        doc.residualValuePercent.value,
        doc.residualValuePercent.maxValue,
        doc.agreedMileage,
        selectedContractTypes
      )
    );
  };

  const onSelectProduct = useCallback(
    product => {
      dispatch(selectSimulation(documentId, siteId, product.productId, product.simulationDocumentId, product.simulationSiteId));
    },
    [dispatch, documentId, siteId]
  );

  const setFavoriteProduct = useCallback(
    (product, priority) => {
      const newProducts = Object.assign([], products);
      newProducts.forEach(x => {
        if (x.productId === product.productId) x.priority = priority;
      });
      setProducts(newProducts);
      dispatch(chooseFavoriteProduct(documentId, siteId, product.simulationDocumentId, product.simulationSiteId, priority));
    },
    [dispatch, documentId, siteId, products]
  );

  const setDeleteProduct = useCallback(
    product => {
      dispatch(deleteProduct(documentId, siteId, product.simulationDocumentId, product.simulationSiteId));
    },
    [dispatch, documentId, siteId]
  );

  const onChangeFinancialProductAdd = useCallback(() => {
    setShowFinancialProductAddModal(!showFinancialProductAddModal);
  }, [showFinancialProductAddModal]);

  const closeContractTypesModal = useCallback(() => setShowContractTypeModal(false), []);

  const onContractTypeLinkClick = useCallback(() => {
    setShowContractTypeModal(true);
  }, []);

  const onPropertyChange = useCallback(
    prop => value => {
      setLocalFilters(prevState =>
        flow(
          editableDocument.update(prop, value, getBusinessRules()),
          editableDocument.validate(getValidationRules(prevState.document), true)
        )(prevState)
      );

      dispatch({ type: actionTypes.SIMULATIONS_FILTERS_MODIFIED });
    },
    [dispatch]
  );

  const onFilterChange = useCallback(
    prop => value => {
      setLocalFilters(prevState =>
        flow(
          editableDocument.update(prop + '.value', value, getBusinessRules()),
          editableDocument.validate(getValidationRules(prevState.document), true)
        )(prevState)
      );

      dispatch({ type: actionTypes.SIMULATIONS_FILTERS_MODIFIED });

      if (prop === 'financingPeriod') {
        dispatch({ type: actionTypes.COMPUTE_OFFER_PRODUCTS_MAX_RESIDUAL_VALUE_STARTED });
        computeMaxResidualValuePercentDebounced(dispatch, documentId, siteId, value);
      }
    },
    [dispatch, documentId, siteId]
  );
  const getOfferFinancialProductVisibilityFields = useCallback(params => {
    if (params.financialProductVisibilityFields) {
      try {
        return JSON.parse(params.financialProductVisibilityFields);
      } catch {
        // Catch Json errors
      }
    }
    return [];
  }, []);

  const onSaveContractTypes = contractTypes => {
    const selectedText = selectedContractTypes
      .filter(x => x.isSelected === true)
      .map(x => {
        return x.contractTypeId;
      })
      .join(', ');
    const contractTypesText = contractTypes
      .filter(x => x.isSelected === true)
      .map(x => {
        return x.contractTypeId;
      })
      .join(', ');
    setSelectedContractTypes(contractTypes);
    if (selectedText !== contractTypesText) dispatch({ type: actionTypes.SIMULATIONS_FILTERS_MODIFIED });
    closeContractTypesModal();
  };

  const areGraceRatesVisible = useCallback(
    params => {
      const graceRatesProperty = getOfferFinancialProductVisibilityFields(params).find(
        vf => vf.propertyName.trim().toUpperCase() === 'GraceRates'.trim().toUpperCase()
      );
      return graceRatesProperty ? graceRatesProperty.isVisible : false;
    },
    [getOfferFinancialProductVisibilityFields]
  );

  const areGuaranteeRatesVisible = useCallback(
    params => {
      const guaranteeRatesProperty = getOfferFinancialProductVisibilityFields(params).find(
        vf => vf.propertyName.trim().toUpperCase() === 'GuaranteeRates'.trim().toUpperCase()
      );
      return guaranteeRatesProperty ? guaranteeRatesProperty.isVisible : false;
    },
    [getOfferFinancialProductVisibilityFields]
  );

  const getNumberOfProducts = useCallback(products => {
    if (!(products && products.length)) return { total: 0, available: 0 };
    const availableProducts = products.filter(prod => prod.isActive);
    return { total: products.length, available: availableProducts.length };
  }, []);

  return (
    <div>
      <ProductSelectionCard
        filterData={localFilters}
        numberOfProducts={getNumberOfProducts(productSelectionData.products)}
        maximumNumberOfProducts={productSelectionData.maximumNumberOfProducts}
        exportSimulationButtonVisible={productSelectionData.exportSimulationButtonVisible}
        documentId={documentId}
        siteId={siteId}
        flags={productSelectionData.flags}
        onPushFilterValues={onPushFilterValues}
        onFilterChange={onFilterChange}
        products={products}
        onSelectProduct={onSelectProduct}
        areGraceRatesVisible={areGraceRatesVisible(params)}
        areGuaranteeRatesVisible={areGuaranteeRatesVisible(params)}
        setProductsViewMode={setProductsViewMode}
        productsViewMode={productsViewMode}
        onChangeFinancialProductAddModal={onChangeFinancialProductAdd}
        showFinancialProductAddModal={showFinancialProductAddModal}
        orderObject={orderObject}
        orderOptions={orderOptions}
        onOrderChange={onOrderChange}
        processing={processing}
        showFilterOfferContracts={productSelectionData.showFilterOfferContracts}
        onContractTypeLinkClick={onContractTypeLinkClick}
        setFavoriteProduct={setFavoriteProduct}
        setDeleteProduct={setDeleteProduct}
        onPropertyChange={onPropertyChange}
        contractTypeNames={selectedContractTypes
          .filter(x => x.isSelected === true)
          .map(x => {
            return x.contractTypeName;
          })
          .join(', ')}
      />
      <>
        {showContractTypesModal && (
          <ContractSelectionModal
            contractTypes={selectedContractTypes}
            closeContractTypesModal={closeContractTypesModal}
            onSaveContractTypes={onSaveContractTypes}
            simulationsGenerated={productSelectionData.simulationsGenerated}
          />
        )}
      </>
    </div>
  );
};

ProductSelectionContainer.propTypes = {
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  t: PropTypes.func.isRequired
};

export default withTranslation()(ProductSelectionContainer);
